import { useState } from "react";
import { COLORS, Wordmark } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { useIsDesktop } from "@/lib/media";
import Footer from "@/components/Footer";

export default function ChangePassword() {
  const { user, changePassword, logout, formatApiError } = useAuth();
  const isDesktop = useIsDesktop();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (next.length < 8) throw new Error("New password must be at least 8 characters");
      if (next !== confirm) throw new Error("Passwords do not match");
      if (next === current) throw new Error("New password must be different from current");
      await changePassword(current, next);
    } catch (e) {
      setError(e?.response ? formatApiError(e) : e.message);
    } finally { setBusy(false); }
  };

  const Form = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", maxWidth: 440 }}>
      <input data-testid="chg-current" className="ls-input" placeholder="Current password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      <input data-testid="chg-new" className="ls-input" placeholder="New password (min 8 chars)" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
      <input data-testid="chg-confirm" className="ls-input" placeholder="Confirm new password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
      {error && <div data-testid="chg-error" style={{ color: "#a02040", fontSize: 13, fontWeight: 700, textAlign: "center" }}>{error}</div>}
      <button data-testid="chg-submit" className="ls-primary-btn" onClick={submit} disabled={busy} style={{ marginTop: 4 }}>
        {busy ? "Please wait…" : "Update password"}
      </button>
      <button onClick={logout} className="no-tap-highlight" style={{ background: "none", border: "none", color: COLORS.purple, fontWeight: 700, fontSize: 13, padding: 8, cursor: "pointer", fontFamily: "'Nunito', sans-serif" }}>
        Sign out
      </button>
    </div>
  );

  if (isDesktop) {
    return (
      <div data-testid="change-password-screen" style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: COLORS.cream }}>
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1.1fr 1fr", minHeight: "80vh" }}>
          <div style={{ background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`, color: "#fff", padding: "80px 60px", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -80, width: 320, height: 320, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
            <div style={{ position: "absolute", bottom: -60, left: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(201,162,75,0.14)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32, position: "relative", zIndex: 1 }}>
              <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 110, borderRadius: 18, boxShadow: "0 20px 50px rgba(0,0,0,0.35)" }} />
              <div>
                <div className="font-heading" style={{ fontWeight: 800, fontSize: 42, letterSpacing: "-1px", lineHeight: 1 }}>
                  Link<span style={{ color: COLORS.goldLight }}>shul</span>
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 14, fontWeight: 700, marginTop: 6 }}>
                  LinkShul
                </div>
              </div>
            </div>
            <h1 className="font-heading" style={{ fontWeight: 800, fontSize: 40, lineHeight: 1.15, marginBottom: 14, position: "relative", zIndex: 1 }}>
              Secure your account
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(255,255,255,0.85)", maxWidth: 520, fontWeight: 500, position: "relative", zIndex: 1 }}>
              For your security, please set a personal password before you continue into the
              LinkShul community portal. Use at least 8 characters and something only you know.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 40px" }}>
            <div style={{ width: "100%", maxWidth: 460 }}>
              <div className="font-heading" style={{ fontWeight: 800, fontSize: 30, color: COLORS.ink, marginBottom: 6 }}>
                Set a new password
              </div>
              <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 14, marginBottom: 28 }}>
                Welcome{user?.first_name ? ", " + user.first_name : ""}. Please choose a new password to continue.
              </div>
              {Form}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Mobile layout (unchanged)
  return (
    <div className="ls-shell" data-testid="change-password-screen">
      <div style={{
        background: `linear-gradient(160deg, ${COLORS.purple} 0%, ${COLORS.plum} 100%)`,
        padding: "40px 24px 26px", borderRadius: "0 0 32px 32px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
      }}>
        <img src="/assets/linkshul-logo.png" alt="LinkShul" style={{ width: 90, borderRadius: 14, boxShadow: "0 12px 30px rgba(0,0,0,0.35)" }} />
        <Wordmark scale={0.85} light />
      </div>
      <div className="ls-scroll" style={{ padding: "26px 22px", height: "calc(100% - 200px)" }}>
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink, textAlign: "center" }}>
          Set a new password
        </div>
        <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13.5, textAlign: "center", marginTop: 6, lineHeight: 1.5 }}>
          Welcome{user?.first_name ? ", " + user.first_name : ""}. Before continuing, please set a new password for your account.
        </div>
        <div style={{ marginTop: 22 }}>
          {Form}
        </div>
      </div>
    </div>
  );
}
